/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import { getBoundedValue, getTranslateValuesFromElement, } from '../functions/index.js';
/**
 * Component that allows to resize parent element.
 * Parent must have `position: relative`.
 * @private
 * @example
 * const ref = React.useRef<HTMLDivElement>(null);
 * return (
 *   <div ref={ref} style={{ position: 'relative' }}>
 *     <Resizer elementRef={ref} />
 *   </div>
 * );
 */
export const Resizer = (props) => {
    const { elementRef, containerRef, onResizeStart, onResizeEnd } = props;
    const isResizing = React.useRef(false);
    const onResizePointerDown = (event) => {
        if (!elementRef.current || event.button !== 0) {
            return;
        }
        const initialPointerX = event.clientX;
        const initialPointerY = event.clientY;
        const [initialTranslateX, initialTranslateY] = getTranslateValuesFromElement(elementRef.current);
        const { width: initialWidth, height: initialHeight } = elementRef.current.getBoundingClientRect();
        let width = `${initialWidth}px`;
        let height = `${initialHeight}px`;
        let translateX = initialTranslateX;
        let translateY = initialTranslateY;
        let minWidth = parseFloat(getComputedStyle(elementRef.current).minWidth);
        if (Number.isNaN(minWidth)) {
            minWidth = 380;
        }
        const minHeight = parseFloat(getComputedStyle(elementRef.current).minHeight);
        const resizer = event.currentTarget.dataset.iuiResizer;
        const ownerDocument = elementRef.current.ownerDocument || document;
        const originalUserSelect = ownerDocument.body.style.userSelect;
        ownerDocument.body.style.userSelect = 'none';
        const onResizePointerMove = (event) => {
            var _a, _b, _c, _d, _e, _f, _g;
            if (!elementRef.current) {
                return;
            }
            if (!isResizing.current) {
                isResizing.current = true;
                onResizeStart === null || onResizeStart === void 0 ? void 0 : onResizeStart();
            }
            const containerRect = (_a = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            const clientX = getBoundedValue(event.clientX, (_b = containerRect === null || containerRect === void 0 ? void 0 : containerRect.left) !== null && _b !== void 0 ? _b : 0, (_d = (_c = containerRect === null || containerRect === void 0 ? void 0 : containerRect.right) !== null && _c !== void 0 ? _c : ownerDocument.documentElement.clientWidth) !== null && _d !== void 0 ? _d : 0);
            const clientY = getBoundedValue(event.clientY, (_e = containerRect === null || containerRect === void 0 ? void 0 : containerRect.top) !== null && _e !== void 0 ? _e : 0, (_g = (_f = containerRect === null || containerRect === void 0 ? void 0 : containerRect.bottom) !== null && _f !== void 0 ? _f : ownerDocument.documentElement.clientHeight) !== null && _g !== void 0 ? _g : 0);
            const diffX = initialPointerX - clientX;
            const diffY = initialPointerY - clientY;
            switch (resizer) {
                case 'top-left': {
                    const newHeight = initialHeight + diffY;
                    if (newHeight >= minHeight) {
                        height = elementRef.current.style.height = `${newHeight}px`;
                        translateY = initialTranslateY - diffY;
                    }
                    const newWidth = initialWidth + diffX;
                    if (newWidth >= minWidth) {
                        width = elementRef.current.style.width = `${newWidth}px`;
                        translateX = initialTranslateX - diffX;
                    }
                    elementRef.current.style.transform = `translate(${translateX}px, ${translateY}px)`;
                    break;
                }
                case 'top': {
                    const newHeight = initialHeight + diffY;
                    if (newHeight < minHeight) {
                        break;
                    }
                    height = elementRef.current.style.height = `${newHeight}px`;
                    translateY = initialTranslateY - diffY;
                    elementRef.current.style.transform = `translate(${translateX}px, ${translateY}px)`;
                    break;
                }
                case 'top-right': {
                    const newHeight = initialHeight + diffY;
                    if (newHeight >= minHeight) {
                        height = elementRef.current.style.height = `${newHeight}px`;
                        translateY = initialTranslateY - diffY;
                    }
                    width = elementRef.current.style.width = `${initialWidth - diffX}px`;
                    elementRef.current.style.transform = `translate(${translateX}px, ${translateY}px)`;
                    break;
                }
                case 'right': {
                    width = elementRef.current.style.width = `${initialWidth - diffX}px`;
                    height = elementRef.current.style.height = `${initialHeight}px`;
                    elementRef.current.style.transform = `translate(${translateX}px, ${translateY}px)`;
                    break;
                }
                case 'bottom-right': {
                    width = elementRef.current.style.width = `${initialWidth - diffX}px`;
                    height = elementRef.current.style.height = `${initialHeight - diffY}px`;
                    elementRef.current.style.transform = `translate(${translateX}px, ${translateY}px)`;
                    break;
                }
                case 'bottom': {
                    height = elementRef.current.style.height = `${initialHeight - diffY}px`;
                    elementRef.current.style.transform = `translate(${translateX}px, ${translateY}px)`;
                    break;
                }
                case 'bottom-left': {
                    const newWidth = initialWidth + diffX;
                    if (newWidth >= minWidth) {
                        width = elementRef.current.style.width = `${newWidth}px`;
                        translateX = initialTranslateX - diffX;
                    }
                    height = elementRef.current.style.height = `${initialHeight - diffY}px`;
                    elementRef.current.style.transform = `translate(${translateX}px, ${translateY}px)`;
                    break;
                }
                case 'left': {
                    const newWidth = initialWidth + diffX;
                    if (newWidth < minWidth) {
                        break;
                    }
                    width = elementRef.current.style.width = `${newWidth}px`;
                    height = elementRef.current.style.height = `${initialHeight}px`;
                    translateX = initialTranslateX - diffX;
                    elementRef.current.style.transform = `translate(${translateX}px, ${translateY}px)`;
                    break;
                }
                default:
                    break;
            }
        };
        ownerDocument.addEventListener('pointermove', onResizePointerMove);
        ownerDocument.addEventListener('pointerup', () => {
            ownerDocument.removeEventListener('pointermove', onResizePointerMove);
            if (elementRef.current) {
                ownerDocument.body.style.userSelect = originalUserSelect;
                isResizing.current = false;
                onResizeEnd === null || onResizeEnd === void 0 ? void 0 : onResizeEnd({
                    width,
                    height,
                    transform: `translate(${translateX}px, ${translateY}px)`,
                });
            }
        }, { once: true });
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { "data-iui-resizer": 'top-left', onPointerDown: onResizePointerDown, style: {
                position: 'absolute',
                top: 0,
                left: 0,
                width: 12,
                height: 12,
                cursor: 'nw-resize',
            } }),
        React.createElement("div", { "data-iui-resizer": 'top', onPointerDown: onResizePointerDown, style: {
                position: 'absolute',
                top: 0,
                left: 8,
                right: 8,
                height: 8,
                cursor: 'n-resize',
            } }),
        React.createElement("div", { "data-iui-resizer": 'top-right', onPointerDown: onResizePointerDown, style: {
                position: 'absolute',
                top: 0,
                right: 0,
                width: 12,
                height: 12,
                cursor: 'ne-resize',
            } }),
        React.createElement("div", { "data-iui-resizer": 'right', onPointerDown: onResizePointerDown, style: {
                position: 'absolute',
                top: 8,
                right: 0,
                bottom: 8,
                width: 8,
                cursor: 'e-resize',
            } }),
        React.createElement("div", { "data-iui-resizer": 'bottom-right', onPointerDown: onResizePointerDown, style: {
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: 12,
                height: 12,
                cursor: 'se-resize',
            } }),
        React.createElement("div", { "data-iui-resizer": 'bottom', onPointerDown: onResizePointerDown, style: {
                position: 'absolute',
                bottom: 0,
                left: 8,
                right: 8,
                height: 8,
                cursor: 's-resize',
            } }),
        React.createElement("div", { "data-iui-resizer": 'bottom-left', onPointerDown: onResizePointerDown, style: {
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: 12,
                height: 12,
                cursor: 'sw-resize',
            } }),
        React.createElement("div", { "data-iui-resizer": 'left', onPointerDown: onResizePointerDown, style: {
                position: 'absolute',
                top: 8,
                left: 0,
                bottom: 8,
                width: 8,
                cursor: 'w-resize',
            } })));
};
export default Resizer;
